import escapeHtml from 'escape-html'

import { ChannelFragment, LaunchContentForExportFragment, MilestoneFragment } from '~generated/operations'
import { formatDate } from '~lib/dateUtils'
import {
  LaunchContentType,
  LaunchModuleContentType,
} from '~modules/launches/containers/DocumentsIntegrationModal/utils'

enum Format {
  HASH = '#',
  PERCENT = '%',
  DOLLAR = '$',
}

const numberCols = new Set([])
const dateCols = new Set(['date', 'startDate', 'endDate'])
const dollarCols = new Set(['budget', 'value', 'Income'])
const formatCols = new Set(['format'])

// maintain one-to-one mapping between targetAudienceCols and targetAudienceDisplayCols
const targetAudienceCols = [
  'name',
  'occupation',
  'avatar',
  'age',
  'demographics',
  'Goals',
  'Frustrations',
  'Jobs-to-be-done',
  'Category Perceptions',
  'Day-to-day',
  'Buying Process',
  'Alternatives',
  'Media Consumption',
]

// maintain one-to-one mapping between targetAudienceCols and targetAudienceDisplayCols
const targetAudienceDisplayCols = [
  'Name',
  'Occupation',
  'Avatar',
  'Age',
  'Demographics',
  'Goals',
  'Frustrations',
  'Jobs To Be Done',
  'Category Perceptions',
  'Day-To-Day',
  'Buying Process',
  'Alternatives',
  'Media Consumption',
]

// maintain one-to-one mapping between demographicsCols and demographicsDisplayCols
const demographicsCols = ['Gender', 'Location', 'Industry', 'Company size', 'Income']

// maintain one-to-one mapping between demographicsCols and demographicsDisplayCols
const demographicsDisplayCols = ['Gender', 'Location', 'Industry', 'Company size', 'Income']

// maintain 1-1 mapping between positioningStrategyCols and positioningStrategyDisplayCols
const positioningStrategyCols = [
  'Alternatives',
  'Attributes',
  'Value',
  'Audience',
  'Problem',
  'Category',
  'Notes',
  'alternatives',
  'attributes',
  'value',
  'audience',
  'problem',
  'category',
  'notes',
]

// maintain 1-1 mapping between positioningStrategyCols and positioningStrategyDisplayCols
const positioningStrategyDisplayCols = [
  'Alternatives',
  'Attributes',
  'Value',
  'Audience',
  'Problem',
  'Category',
  'Notes',
  'Alternatives',
  'Attributes',
  'Value',
  'Audience',
  'Problem',
  'Category',
  'Notes',
]

// maintain 1-1 mapping between promotionChannelsCols and promotionChannelslDisplayCols
const promotionChannelsCols = ['name', 'description', 'budget', 'startDate', 'endDate']

// maintain 1-1 mapping between promotionChannelsCols and promotionChannelslDisplayCols
const promotionChannelslDisplayCols = ['Name', 'Description', 'Budget', 'Start Date', 'End Date']

// maintain 1-1 mapping between budgetAllocationCols and budgetAllocationDisplayCols
const budgetAllocationCols = ['name', 'value']

// maintain 1-1 mapping between budgetAllocationCols and budgetAllocationDisplayCols
const budgetAllocationDisplayCols = ['Activity', 'Budget']

// maintain 1-1 mapping between majorMilestonesCols and majorMilestonesDisplayCols
const majorMilestonesCols = ['name', 'startDate']

// maintain 1-1 mapping between majorMilestonesCols and majorMilestonesDisplayCols
const majorMilestonesDisplayCols = ['Milestone name (e.g. Planning Kickoff, Beta, etc.)  ', 'Date']

// maintain 1-1 mapping between kpiCols and kpiDisplayCols
const kpiCols = ['metric', 'format', 'target', 'actual']

// maintain 1-1 mapping between kpiCols and kpiDisplayCols
const kpiDisplayCols = ['Metric', 'Format', 'Target', 'Actual']

const html2Xhtml = (htmlString: string) => {
  const s = new XMLSerializer()
  const parser = new DOMParser()

  const document = parser.parseFromString(htmlString, 'text/html')

  const xhtml = s.serializeToString(document)

  return xhtml
}

const getHeading = (heading: string): string => {
  return `<p><h1>${heading}</h1></p>`
}

const getSubHeading = (subHeading: string): string => {
  return `<p><h2>${subHeading}</h2></p>`
}

const getHeadingH3 = (headingH3: string): string => {
  return `<p><h3>${headingH3}</h3></p>`
}

const getParagraph = (content: string): string => {
  return `<p>${escapeHtml(content)}</p>`
}

const getParagraphUnescaped = (content: string): string => {
  return `<p>${content}</p>`
}

const getTableElement = (val: string) => {
  return `<td>${escapeHtml(val)}</td>`
}

const formatCol = (col: string, obj: Record<string, any>) => {
  let val = ''
  if (obj === undefined) return val

  if (numberCols.has(col)) {
    val = col in obj ? (obj[col] ? obj[col].toString() : '') : ''
  } else if (dateCols.has(col)) {
    val = col in obj ? formatDate(obj[col], 'day2DigitsMonthShort') : ''
  } else if (dollarCols.has(col)) {
    val = col in obj ? (obj[col] ? `$${obj[col].toLocaleString()}` : '') : ''
  } else if (formatCols.has(col)) {
    val = col in obj ? (Format[obj[col]] ? Format[obj[col]] : obj[col]?.toString()) : ''
  } else {
    val = col in obj ? (obj[col] ? obj[col].toString() : '') : ''
  }

  return val
}

const getTableRows = (data: Record<string, any>[], cols: string[]): string => {
  let tableRows = ''
  data.forEach((o) => {
    let tableRow = ''
    cols.forEach((k) => {
      const val = formatCol(k, o)
      tableRow += getTableElement(val)
    })
    tableRows += `<tr>${tableRow}</tr>`
  })
  return tableRows
}

const getTableHeader = (cols: string[]): string => {
  let tableHeader = ''

  cols.forEach((c) => {
    tableHeader += `<th><b>${c}</b></th>`
  })

  return `<tr>${tableHeader}</tr>`
}

const getTable = (tableHeader: string, tableBody: string): string => {
  return `<table><tbody>${tableHeader}${tableBody}</tbody></table>`
}

const formatLaunchSummary = (launchSummary: string): string => {
  let html = ''

  try {
    if (launchSummary.length > 0) {
      const heading = getHeading('Launch Summary')
      html += heading

      const body = getParagraphUnescaped(launchSummary)
      html += body
    }

    return html
  } catch {
    return html
  }
}

const formatMajorMilestones = (majorMilestones: Record<string, any>[]): string => {
  let html = ''

  try {
    const dataPresent = majorMilestones.length > 0

    if (dataPresent) {
      const heading = getHeading('Major Milestones')
      html += heading

      const tableHeader = getTableHeader(majorMilestonesDisplayCols)
      const tableRows = getTableRows(majorMilestones, majorMilestonesCols)
      const tableHtml = getTable(tableHeader, tableRows)

      html += tableHtml
    }

    return html
  } catch {
    return html
  }
}

const formatLaunchObjectives = (launchObjectives: string): string => {
  let html = ''

  try {
    if (launchObjectives.length > 0) {
      const heading = getHeading('Launch Objectives')
      html += heading

      const body = getParagraphUnescaped(launchObjectives)
      html += body
    }

    return html
  } catch {
    return html
  }
}

const formatKeyMetricsAndKPIs = (keyMetricsAndKPIs: Record<string, any>[]): string => {
  let html = ''

  try {
    const dataPresent = keyMetricsAndKPIs.length > 0

    if (dataPresent) {
      const heading = getHeading('Key Metrics and KPIs')
      html += heading

      const tableHeader = getTableHeader(kpiDisplayCols)
      const tableRows = getTableRows(keyMetricsAndKPIs, kpiCols)
      const tableHtml = getTable(tableHeader, tableRows)

      html += tableHtml
    }

    return html
  } catch {
    return html
  }
}

const formatMessaging = (messaging: string): string => {
  let html = ''

  try {
    if (messaging.length > 0) {
      const heading = getHeading('Messaging')
      html += heading

      const body = getParagraphUnescaped(messaging)
      html += body
    }

    return html
  } catch {
    return html
  }
}

const formatPricingAndPackaging = (pricingAndPackaging: string): string => {
  let html = ''

  try {
    if (pricingAndPackaging.length > 0) {
      const heading = getHeading('Pricing and Packaging')
      html += heading

      const body = getParagraphUnescaped(pricingAndPackaging)
      html += body
    }

    return html
  } catch {
    return html
  }
}

const formatBudgetAllocation = (budgetAllocation: Record<string, any>[]): string => {
  let html = ''

  try {
    const dataPresent = budgetAllocation.length > 0

    if (dataPresent) {
      const heading = getHeading('Budget Allocation')
      html += heading
      const tableHeader = getTableHeader(budgetAllocationDisplayCols)
      const tableRows = getTableRows(budgetAllocation, budgetAllocationCols)
      const tableHtml = getTable(tableHeader, tableRows)

      html += tableHtml
    }

    return html
  } catch {
    return html
  }
}

const formatPromotionChannels = (promotionChannels: Record<string, any>[]): string => {
  let html = ''

  try {
    const dataPresent = promotionChannels.length > 0 && Object.keys(promotionChannels[0]?.timeframes[0]).length > 0

    if (dataPresent) {
      const channelsData = [{}]
      promotionChannels.forEach((pc) => {
        channelsData.push(pc.timeframes[0])
      })
      const heading = getHeading('Promotion Channels')
      html += heading

      const tableHeader = getTableHeader(promotionChannelslDisplayCols)
      const tableRows = getTableRows(channelsData, promotionChannelsCols)
      const tableHtml = getTable(tableHeader, tableRows)

      html += tableHtml
    }

    return html
  } catch {
    return html
  }
}

const formatPositioningStrategy = (positioningStrategy: Record<string, any>[]): string => {
  let html = ''

  try {
    const data = {}
    positioningStrategy?.forEach((p) => {
      data[p.title] = p.text
    })

    let body = ''
    Object.keys(data)?.forEach((k) => {
      positioningStrategyCols.forEach((c, i) => {
        if (c === k) {
          body += getHeadingH3(positioningStrategyDisplayCols[i] || null)
          body += getParagraph(data[k])
        }
      })
    })

    if (body.length > 0) {
      const heading = getHeading('Positioning Strategy')
      html += heading
      html += body
    }

    return html
  } catch {
    return html
  }
}

const formatAge = (age: number[]): string => {
  let body = ''

  try {
    body += getHeadingH3('Age')
    body += `<p>${age[0]} to ${age[1]}</p>`

    return body
  } catch {
    return body
  }
}

const formatDemographics = (demographics: Record<string, any>[]): string => {
  let body = ''

  try {
    body += getHeadingH3('Demographics')
    const tableHeader = getTableHeader(demographicsDisplayCols)
    const demograpicsData = {}
    demographics?.forEach((e) => {
      demographicsCols.forEach((k) => {
        if (k === e.name) {
          demograpicsData[k] = e.value
        }
      })
    })
    const tableRows = getTableRows([demograpicsData], demographicsCols)
    const tableHtml = getTable(tableHeader, tableRows)
    body += tableHtml

    return body
  } catch {
    return body
  }
}

const formatAvatar = (url: string, serviceName: string) => {
  let body = ''

  try {
    if (serviceName === 'CONFLUENCE' && url) {
      body += `<p><ac:image ac:class="fit-picture" ac:height="400" ac:width="600"><ri:url ri:value="${url}" /></ac:image></p>`
    } else if (serviceName === 'GOOGLE' && url) {
      body += `<p><img src="${url}" class="fit-picture" height="400" width="600" /></p>`
    }

    return body
  } catch {
    return body
  }
}

const formatBlocksData = (blocksData: Record<string, any>[]) => {
  let body = ''

  try {
    blocksData?.forEach((b: Record<string, any>) => {
      const idx = targetAudienceCols.indexOf(b.title)
      if (idx > -1) {
        body += getHeadingH3(targetAudienceDisplayCols[idx])
        body += getParagraph(b.text)
      }
    })

    return body
  } catch {
    return body
  }
}

const formatTargetAudience = (targetAudience: Record<string, any>[], serviceName: string): string => {
  let html = ''

  try {
    const dataPresent = !(targetAudience.length === 1 && Object.keys(targetAudience[0]).length === 0)

    if (dataPresent) {
      const heading = getHeading('Target Audience')
      html += heading

      let body = ''

      targetAudience?.forEach((e) => {
        body += getSubHeading(`Audience: ${e.name || ''}`)
        if (e.occupation) {
          body += getHeadingH3('Occupation')
          body += getParagraph(e.occupation)
        }
        if (e.age) body += formatAge(e.age)
        if (e.demographics) body += formatDemographics(e.demographics)
        if (e.avatar) body += formatAvatar(e.avatar, serviceName)
        if (e.blocksData) body += formatBlocksData(e.blocksData)
      })

      html += body
    }
    return html
  } catch {
    return html
  }
}

export const formatModule = (pageTitle: string, content: LaunchModuleContentType, serviceName: string): string => {
  let html = ''

  if (pageTitle === 'Launch Summary') {
    html = formatLaunchSummary(content as string)
  } else if (pageTitle === 'Major Milestones') {
    html = formatMajorMilestones(content as Record<string, any>[])
  } else if (pageTitle === 'Launch Objectives') {
    html = formatLaunchObjectives(content as string)
  } else if (pageTitle === 'Key Metrics & KPIs' || pageTitle === 'Key Metrics and KPIs') {
    html = formatKeyMetricsAndKPIs(content as Record<string, any>[])
  } else if (pageTitle === 'Messaging') {
    html = formatMessaging(content as string)
  } else if (pageTitle === 'Pricing & Packaging' || pageTitle === 'Pricing and Packaging') {
    html = formatPricingAndPackaging(content as string)
  } else if (pageTitle === 'Budget Allocation') {
    html = formatBudgetAllocation(content as Record<string, any>[])
  } else if (
    pageTitle === 'Promotion Channels T1' ||
    pageTitle === 'Promotion Channels T2' ||
    pageTitle === 'Promotion Channels T3' ||
    pageTitle === 'Promotion Channels'
  ) {
    html = formatPromotionChannels(content as Record<string, any>[])
  } else if (pageTitle === 'Positioning Strategy') {
    html = formatPositioningStrategy(content as Record<string, any>[])
  } else if (pageTitle === 'Target Audience') {
    html = formatTargetAudience(content as Record<string, any>[], serviceName)
  }

  if (serviceName === 'CONFLUENCE') {
    const xhtml = html2Xhtml(html)
    return xhtml
  }

  return html
}

export const formatLaunch = (content: LaunchContentType, serviceName: string): string => {
  let html = ''

  Object.keys(content)?.forEach((k) => {
    if (k === 'Launch Summary') {
      const launchSummary = content['Launch Summary']
      if (launchSummary) html += formatLaunchSummary(launchSummary)
    }

    if (k === 'Major Milestones') {
      const majorMilestones = content['Major Milestones']
      if (majorMilestones) html += formatMajorMilestones(majorMilestones)
    }

    if (k === 'Launch Objectives') {
      const launchObjectives = content['Launch Objectives']
      if (launchObjectives) html += formatLaunchObjectives(launchObjectives)
    }

    if (k === 'Key Metrics & KPIs') {
      const kpis = content['Key Metrics & KPIs']
      if (kpis) html += formatKeyMetricsAndKPIs(kpis)
    }

    if (k === 'Target Audience') {
      const targetAudience = content['Target Audience']
      if (targetAudience) html += formatTargetAudience(targetAudience, serviceName)
    }

    if (k === 'Positioning Strategy') {
      const positioningStrategy = content['Positioning Strategy']
      if (positioningStrategy) html += formatPositioningStrategy(positioningStrategy)
    }

    if (k === 'Messaging') {
      // eslint-disable-next-line dot-notation
      const messaging = content['Messaging']
      if (messaging) html += formatMessaging(messaging)
    }

    if (k === 'Pricing & Packaging') {
      const pricing = content['Pricing & Packaging']
      if (pricing) html += formatPricingAndPackaging(pricing)
    }

    if (k === 'Promotion Channels T1') {
      const promotionChannels = content['Promotion Channels T1']
      if (promotionChannels) html += formatPromotionChannels(promotionChannels)
    }

    if (k === 'Promotion Channels T2') {
      const promotionChannels = content['Promotion Channels T2']
      if (promotionChannels) html += formatPromotionChannels(promotionChannels)
    }

    if (k === 'Promotion Channels T3') {
      const promotionChannels = content['Promotion Channels T3']
      if (promotionChannels) html += formatPromotionChannels(promotionChannels)
    }

    if (k === 'Promotion Channels') {
      const promotionChannels = content['Promotion Channels']
      if (promotionChannels) html += formatPromotionChannels(promotionChannels)
    }

    if (k === 'Budget Allocation') {
      const budgetAllocation = content['Budget Allocation']
      if (budgetAllocation) html += formatBudgetAllocation(budgetAllocation)
    }
  })

  if (serviceName === 'CONFLUENCE') {
    const xhtml = html2Xhtml(html)
    return xhtml
  }

  return html
}

export function formatLaunchContent(launchContent: LaunchContentForExportFragment) {
  return launchContent.sections.reduce((acc, section) => {
    const sectionHeading = `<h2>${section.name}</h2>`
    const sectionContent = section.modules.reduce((acc, launchModule) => {
      const moduleHeading = `<h3>${launchModule.name}</h3>`
      const moduleContent = launchModule.contentHTML
      return `${acc}${moduleHeading}${moduleContent}`
    }, '')

    return `${acc}${sectionHeading}${sectionContent}`
  }, '')
}

export interface ExportLaunchArgs {
  launchContent: LaunchContentForExportFragment
  milestones: MilestoneFragment[]
  channels: ChannelFragment[]
}

export function formatExportLaunchContent({ launchContent, milestones, channels }: ExportLaunchArgs) {
  const launchContentHtml = formatLaunchContent(launchContent)
  const milestonesHtml = formatMajorMilestones(milestones)
  const channelsHtml = formatPromotionChannels(channels)

  return `${launchContentHtml}${milestonesHtml}${channelsHtml}`
}

export interface ExportLaunchModuleArgs {
  launchModule: ArrayElement<ArrayElement<LaunchContentForExportFragment['sections']>['modules']>
}

export function formatExportLaunchModuleContent({ launchModule }: ExportLaunchModuleArgs) {
  const moduleHeading = `<h3>${launchModule.name}</h3>`
  const moduleContent = launchModule.contentHTML
  return `${moduleHeading}${moduleContent}`
}
